import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ApiClientError, type DeviceView } from '@olmt/shared'
import { api } from '../lib/api'
import { saveDisplayName, savePrivacy, signOut, uploadAvatar } from '../lib/data'
import { LOCALES, t, useLocaleStore, useT } from '../lib/i18n'
import { formatPresence } from '../lib/time'
import { useAuthStore } from '../stores/authStore'
import { Avatar } from '../components/Avatar'
import { IconButton } from '../components/IconButton'
import { Spinner } from '../components/Spinner'
import { ArrowLeftIcon } from '../components/Icons'

/**
 * The account screen (docs/CONTRACT.md §4 `/api/me*`). The phone's settings screen is the model: the
 * signed-in person's profile, the devices their account is signed in on, their password, and — W13 —
 * what they share with everyone else.
 *
 * The session list is the one place a destructive control lands, so it is two-step the same way the
 * chat details' removal is: the first press *arms* an inline confirm next to the row, and only its
 * filled `danger` button acts. The row the api names as the caller's own (§4 `deviceId`) is marked
 * "This device" and its confirm says plainly that revoking it signs this browser out.
 *
 * Every write reports back: a save shows "Name saved.", the avatar paints its own fraction, a
 * revocation re-reads the list. Nothing here optimistically pretends.
 */
export function AccountPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const t = useT()
  const choice = useLocaleStore((state) => state.choice)
  const setLocale = useLocaleStore((state) => state.setLocale)

  const [name, setName] = useState(user?.displayName ?? '')
  const [savingName, setSavingName] = useState(false)
  const [nameNotice, setNameNotice] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [avatarProgress, setAvatarProgress] = useState<number | null>(null)
  const avatarInput = useRef<HTMLInputElement>(null)

  const [devices, setDevices] = useState<DeviceView[]>([])
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null)
  const [devicesLoading, setDevicesLoading] = useState(true)
  const [devicesError, setDevicesError] = useState<string | null>(null)
  const [armedDeviceId, setArmedDeviceId] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  // W13 — what the member shares, as `GET /api/me` reported it (see `AuthState.privacy`). The boot
  // read and every accepted patch write it, so a toggle changed on another device shows its real
  // state here rather than a local guess.
  const privacy = useAuthStore((state) => state.privacy)
  const [privacyBusy, setPrivacyBusy] = useState<keyof typeof privacy | null>(null)
  const [privacyNotice, setPrivacyNotice] = useState<string | null>(null)
  const [privacyError, setPrivacyError] = useState<string | null>(null)

  const refreshDevices = useCallback(async () => {
    setDevicesError(null)
    try {
      const response = await api.devices()
      setDevices(response.devices)
      // §4: the row behind this bearer token, so the list marks it instead of guessing by order.
      setCurrentDeviceId(response.deviceId)
    } catch {
      setDevicesError(t('account.errorLoadSessions'))
    } finally {
      setDevicesLoading(false)
    }
  }, [t])

  useEffect(() => {
    void refreshDevices()
  }, [refreshDevices])

  const submitName = async (event: FormEvent) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed === '' || trimmed.length > 64) {
      setNameError(t('account.errorNameLength'))
      setNameNotice(null)
      return
    }
    setSavingName(true)
    setNameError(null)
    setNameNotice(null)
    try {
      await saveDisplayName(trimmed)
      setName(trimmed)
      setNameNotice(t('account.nameSaved'))
    } catch {
      setNameError(t('account.errorSaveName'))
    } finally {
      setSavingName(false)
    }
  }

  const pickAvatar = async (file: File | undefined) => {
    if (!file) return
    setAvatarProgress(0)
    setNameError(null)
    setNameNotice(null)
    try {
      await uploadAvatar(file, setAvatarProgress)
    } catch {
      setNameError(t('account.errorUploadPhoto'))
    } finally {
      setAvatarProgress(null)
      // Clear the input so picking the same file twice still fires `change`.
      if (avatarInput.current) avatarInput.current.value = ''
    }
  }

  const confirmRevoke = async (device: DeviceView) => {
    setRevoking(true)
    setDevicesError(null)
    try {
      await api.deleteDevice(device.id)
      // Revoking this browser is signing out of it: the token no longer maps to a device, so the
      // session is dropped here rather than left to fail on the next request.
      if (device.id === currentDeviceId) {
        await signOut()
        void navigate('/login', { replace: true })
        return
      }
      setArmedDeviceId(null)
      await refreshDevices()
    } catch {
      setDevicesError(t('account.errorRevoke'))
    } finally {
      setRevoking(false)
    }
  }

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault()
    if (newPassword.length < 8) {
      setPasswordError(t('account.errorNewPasswordShort'))
      setPasswordNotice(null)
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('account.errorPasswordMismatch'))
      setPasswordNotice(null)
      return
    }
    setSavingPassword(true)
    setPasswordError(null)
    setPasswordNotice(null)
    try {
      await api.changePassword({ currentPassword, newPassword })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordNotice(t('account.passwordChanged'))
    } catch (error) {
      /* `bad_credentials` here is the change-password route's flat 401 for a wrong *current*
         password (docs/CONTRACT.md §4: `PATCH /api/me/password` answers the code login answers), so
         it keeps its own sentence — the front door's sign-in wording (`auth.errorBadCredentials`)
         would be the wrong thing to say about the password being replaced. */
      setPasswordError(
        error instanceof ApiClientError && error.code === 'bad_credentials'
          ? t('account.errorCurrentPassword')
          : t('account.errorChangePassword'),
      )
    } finally {
      setSavingPassword(false)
    }
  }

  /**
   * W13 — one switch flipped. The request carries only the field that moved, which is what the api's
   * both-optional body is for: the other toggle keeps whatever the server has. Nothing on screen
   * moves until the answer lands (`savePrivacy` writes the store), so a refusal leaves the switch
   * where it was and says so — the page's rule for every other write.
   */
  const togglePrivacy = async (field: keyof typeof privacy, next: boolean) => {
    setPrivacyBusy(field)
    setPrivacyNotice(null)
    setPrivacyError(null)
    try {
      await savePrivacy(field === 'shareLastSeen' ? { shareLastSeen: next } : { shareReadReceipts: next })
      setPrivacyNotice(t('account.privacySaved'))
    } catch {
      setPrivacyError(t('account.errorPrivacy'))
    } finally {
      setPrivacyBusy(null)
    }
  }

  const nameChanged = name.trim() !== '' && name.trim() !== user?.displayName
  return (
    <div className="app-shell">
      <header className="app-header flex items-center gap-2 border-b border-line bg-surface">
        <IconButton label={t('account.backToChats')} onClick={() => void navigate('/')}>
          <ArrowLeftIcon size={22} />
        </IconButton>
        <h1 className="flex-1 text-title font-semibold text-ink">{t('account.title')}</h1>
      </header>

      <main className="chat-scroll">
        <div className="room-column flex flex-col gap-6 px-4 py-5 text-ui">
          {/* ------------------------------------------------------------ profile */}
          <section className="flex flex-col gap-2">
            <h2 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">{t('account.profile')}</h2>

            {nameError && <Banner tone="danger">{nameError}</Banner>}
            {nameNotice && <Banner tone="ok">{nameNotice}</Banner>}

            <div className="rounded-panel bg-surface p-4 ring-1 ring-line ring-inset">
              <div className="flex items-center gap-3.5">
                <button
                  type="button"
                  disabled={avatarProgress !== null}
                  onClick={() => avatarInput.current?.click()}
                  className="press relative shrink-0 rounded-full ring-1 ring-line-strong ring-inset disabled:opacity-60"
                  aria-label={t('account.changePhoto')}
                >
                  <Avatar name={user?.displayName ?? t('account.me')} path={user?.avatarUrl ?? null} size={72} />
                  {avatarProgress !== null && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-app/70 text-meta font-semibold text-accent tabular-nums">
                      {Math.round(avatarProgress * 100)}%
                    </span>
                  )}
                </button>
                <span className="min-w-0">
                  <span className="block truncate text-body font-semibold text-ink">
                    <bdi>{user?.displayName ?? t('account.me')}</bdi>
                  </span>
                  {user?.handle && <span className="block truncate text-meta text-muted">@{user.handle}</span>}
                  <span className="mt-1 block text-meta text-muted">{t('account.tapPhoto')}</span>
                </span>
              </div>
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => void pickAvatar(event.target.files?.[0])}
              />

              <form className="mt-4 flex flex-col gap-2" onSubmit={(event) => void submitName(event)}>
                <label htmlFor="account-name" className="text-meta text-muted">
                  {t('account.displayName')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="account-name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={64}
                    autoComplete="name"
                    className="min-h-11 min-w-0 flex-1 rounded-well bg-surface2 px-3.5 text-body text-ink ring-1 ring-line ring-inset"
                  />
                  <button
                    type="submit"
                    disabled={savingName || !nameChanged}
                    className="press flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-40"
                  >
                    {savingName && <Spinner size={16} label={t('account.saving')} />}
                    {t('account.save')}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* ------------------------------------------------------------ sessions */}
          <section className="flex flex-col gap-2">
            <h2 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">{t('account.sessions')}</h2>

            {devicesError && <Banner tone="danger">{devicesError}</Banner>}

            <ul className="overflow-hidden rounded-panel bg-surface ring-1 ring-line ring-inset">
              {devicesLoading ? (
                <li className="flex items-center gap-2.5 px-4 py-4 text-muted">
                  <Spinner label={t('account.loadingSessions')} size={18} />
                  {t('account.loading')}
                </li>
              ) : devices.length === 0 ? (
                <li className="px-4 py-4 text-muted">{t('account.noDevices')}</li>
              ) : (
                devices.map((device) => (
                  <DeviceRow
                    key={device.id}
                    device={device}
                    thisDevice={device.id === currentDeviceId}
                    armed={armedDeviceId === device.id}
                    busy={revoking}
                    onArm={() => setArmedDeviceId(device.id)}
                    onCancel={() => setArmedDeviceId(null)}
                    onConfirm={() => void confirmRevoke(device)}
                  />
                ))
              )}
            </ul>
            <p className="px-1 text-meta text-muted">{t('account.sessionsHint')}</p>
          </section>

          {/* ------------------------------------------------------------ password */}
          <section className="flex flex-col gap-2">
            <h2 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">{t('account.password')}</h2>

            {passwordError && <Banner tone="danger">{passwordError}</Banner>}
            {passwordNotice && <Banner tone="ok">{passwordNotice}</Banner>}

            <form
              className="flex flex-col gap-3 rounded-panel bg-surface p-4 ring-1 ring-line ring-inset"
              onSubmit={(event) => void submitPassword(event)}
            >
              <PasswordField
                id="account-current-password"
                label={t('account.currentPassword')}
                autoComplete="current-password"
                value={currentPassword}
                onChange={setCurrentPassword}
              />
              <PasswordField
                id="account-new-password"
                label={t('account.newPassword')}
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
              />
              <PasswordField
                id="account-confirm-password"
                label={t('account.confirmPassword')}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={setConfirmPassword}
              />
              <p className="text-meta text-muted">{t('account.passwordHint')}</p>
              <button
                type="submit"
                disabled={savingPassword || currentPassword === '' || newPassword === ''}
                className="press flex min-h-11 items-center justify-center gap-2 self-start rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-40"
              >
                {savingPassword && <Spinner size={16} label={t('account.changingPassword')} />}
                {t('account.changePassword')}
              </button>
            </form>
          </section>

          {/* ------------------------------------------------------------ language */}
          <section className="flex flex-col gap-2">
            <h2 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">{t('account.language')}</h2>
            <div className="flex flex-col gap-2 rounded-panel bg-surface p-4 ring-1 ring-line ring-inset">
              <div className="flex flex-wrap gap-2">
                {LOCALES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={choice === option.id}
                    onClick={() => setLocale(option.id)}
                    className={`min-h-11 rounded-full px-4 text-ui font-medium ring-1 ring-inset transition-colors duration-quick ${
                      choice === option.id
                        ? 'bg-surface3 text-ink ring-line-strong'
                        : 'bg-surface2 text-muted ring-line hover:bg-surface3'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-meta text-muted">{t('account.languageHint')}</p>
            </div>
          </section>

          {/* ------------------------------------------------------------ privacy */}
          {/* W13. Two switches, one panel — the same shape the language row above uses for a setting
              the member owns, and the pages' rule here too: the switch moves on the server's answer,
              not on the press, and a refusal is said in the page's own danger panel. */}
          <section className="flex flex-col gap-2">
            <h2 className="px-1 text-meta font-semibold tracking-wide text-muted uppercase">{t('account.privacy')}</h2>

            {privacyError && <Banner tone="danger">{privacyError}</Banner>}
            {privacyNotice && <Banner tone="ok">{privacyNotice}</Banner>}

            <div className="flex flex-col overflow-hidden rounded-panel bg-surface ring-1 ring-line ring-inset">
              <PrivacySwitch
                id="account-last-seen"
                label={t('account.lastSeen')}
                hint={t('account.lastSeenHint')}
                checked={privacy.shareLastSeen}
                disabled={privacyBusy !== null}
                onToggle={(next) => void togglePrivacy('shareLastSeen', next)}
              />
              <PrivacySwitch
                id="account-read-receipts"
                label={t('account.readReceipts')}
                hint={t('account.readReceiptsHint')}
                checked={privacy.shareReadReceipts}
                disabled={privacyBusy !== null}
                onToggle={(next) => void togglePrivacy('shareReadReceipts', next)}
              />
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

type PrivacySwitchProps = {
  id: string
  label: string
  hint: string
  checked: boolean
  disabled: boolean
  onToggle: (next: boolean) => void
}

/**
 * W13 — one switch in the app's own language. `role="switch"` with `aria-checked` is what a screen
 * reader reads as on/off; the row's name comes from the label it is pointed at and the one-line hint
 * from below it, so the explanation is part of the control rather than a sentence floating near it.
 * The knob is placed with `justify-start`/`justify-end` — logical, so an Arabic chrome mirrors it
 * without a second rule — and the state is a shape and a hue, never the hue alone (§6.2, §6.8).
 */
function PrivacySwitch({ id, label, hint, checked, disabled, onToggle }: PrivacySwitchProps) {
  return (
    <div className="flex items-start gap-3 border-b border-line px-4 py-3.5 last:border-b-0">
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span id={`${id}-label`} className="text-ui font-medium text-ink">
          {label}
        </span>
        <span id={`${id}-hint`} className="text-meta text-muted">
          {hint}
        </span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        disabled={disabled}
        onClick={() => onToggle(!checked)}
        className="press -my-1 -me-1 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50"
      >
        <span
          aria-hidden="true"
          className={`flex h-6 w-11 items-center rounded-full p-0.5 ring-1 ring-inset transition-colors duration-quick ${
            checked
              ? 'justify-end bg-accent ring-accent'
              : 'justify-start bg-surface2 ring-line-strong'
          }`}
        >
          <span className={`size-5 rounded-full ${checked ? 'bg-on-accent' : 'bg-muted'}`} />
        </span>
      </button>
    </div>
  )
}

/** A tinted panel: `danger` for a failure, the lamp for a confirmation (§"Signals"). */
function Banner({ tone, children }: { tone: 'ok' | 'danger'; children: ReactNode }) {
  const danger = tone === 'danger'
  return (
    <p
      role={danger ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-panel px-3 py-2 text-ui ring-1 ring-inset ${
        danger ? 'bg-danger/10 text-danger ring-danger/30' : 'bg-accent/10 text-accent ring-accent/25'
      }`}
    >
      <span aria-hidden="true" className={`mt-1.5 size-1.5 shrink-0 rounded-full ${danger ? 'bg-danger' : 'bg-accent'}`} />
      <span>{children}</span>
    </p>
  )
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
}: {
  id: string
  label: string
  autoComplete: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-meta text-muted">{label}</span>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 rounded-well bg-surface2 px-3.5 text-body text-ink ring-1 ring-line ring-inset"
      />
    </label>
  )
}

type DeviceRowProps = {
  device: DeviceView
  thisDevice: boolean
  armed: boolean
  busy: boolean
  onArm: () => void
  onCancel: () => void
  onConfirm: () => void
}

/** The mark on the row `GET /api/me` names as the caller's own (§4). */
function DeviceRow({ device, thisDevice, armed, busy, onArm, onCancel, onConfirm }: DeviceRowProps) {
  const t = useT()
  const name = device.name || t('account.unnamedDevice')
  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="min-w-0 truncate text-ui font-medium text-ink">
              <bdi>{name}</bdi>
            </span>
            {thisDevice && (
              <span className="shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-meta font-medium text-accent ring-1 ring-accent/25 ring-inset">
                {t('account.thisDevice')}
              </span>
            )}
          </span>
          <span className="block truncate text-meta text-muted">{deviceSubtitle(device)}</span>
        </span>
        <button
          type="button"
          onClick={onArm}
          aria-label={t('account.revokeDevice', { name })}
          className="press min-h-11 shrink-0 rounded-full px-3 text-ui font-medium text-danger transition-colors duration-quick hover:bg-danger/10"
        >
          {t('account.revoke')}
        </button>
      </div>
      {armed && (
        <div className="flex flex-col gap-2.5 bg-danger/10 px-4 pt-3 pb-4 ring-1 ring-danger/25 ring-inset">
          <p className="text-ui text-danger">
            {thisDevice
              ? t('account.revokeThisDeviceConfirm', { name })
              : t('account.revokeDeviceConfirm', { name })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onConfirm}
              className="press flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-ui font-semibold text-on-danger hover:brightness-110 disabled:opacity-50"
            >
              {busy && <Spinner size={16} label={t('account.revoking')} />}
              {t('account.revoke')}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
            >
              {t('account.cancel')}
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * The device line: platform and when it was last seen, both as chrome. A platform the client does
 * not recognise is shown as it arrived rather than swallowed.
 */
function deviceSubtitle(device: DeviceView): string {
  const platform = device.platform ?? t('account.unknownPlatform')
  const seen = device.lastSeenAt ? formatPresence(false, device.lastSeenAt) : t('account.neverSeen')
  return `${platform} · ${seen}`
}
